// The issue form's location picker, as a value: which areas are picked and the
// points pinned on their floor plans. Each app renders it (a popper on the web,
// a sheet on the phone); the rules for how a tap changes it live here.

import type { IConfigArea, ISelectedPoint } from '../../trusstor/index.js';
import { sortFloors } from '../utils/sortFloors.js';
import { toIssueLocation, type IssueLocation } from './issueLocation.js';

/** A dropped pin is the shared selected-point shape (`{ xPercentage, yPercentage }`). */
export type Pin = ISelectedPoint;

/** A floor's areas, in the order the site lists them. */
export interface IFloorGroup {
  floorId: string;
  floorNick: string;
  areas: IConfigArea[];
}

/**
 * `selected` holds the `${floorId}:${areaId}` keys of the picked areas; `pins`
 * the points dropped on each, under the same key. The two move together: an
 * area with a pin is always selected, un-picking an area drops its pins, and
 * clearing a floor wipes both.
 */
export interface ISelectionState {
  selected: ReadonlySet<string>;
  pins: Readonly<Record<string, Pin[]>>;
}

export function areaKey(floorId: string, areaId: string): string {
  return `${floorId}:${areaId}`;
}

/** Areas within a floor: Trusstor's own sort index first, then by name. */
const byAreaOrder = (a: IConfigArea, b: IConfigArea) =>
  (a.areaSortIndex ?? Infinity) - (b.areaSortIndex ?? Infinity) || a.areaNick.localeCompare(b.areaNick, undefined, { numeric: true });

/** The site's areas grouped by floor — floors in Trusstor's order (-1, 1, 2 … 10,
 *  lettered, PA, special), each floor's areas in its own. */
export function buildFloorGroups(options: readonly IConfigArea[]): IFloorGroup[] {
  const map = new Map<string, IFloorGroup & { isSpecialFloor?: boolean }>();
  for (const area of options) {
    const group = map.get(area.floorId);
    if (group) group.areas.push(area);
    else
      map.set(area.floorId, {
        floorId: area.floorId,
        floorNick: area.floorNick ?? area.floorId,
        isSpecialFloor: area.isSpecialFloor,
        areas: [area],
      });
  }
  return sortFloors([...map.values()], 'asc').map(({ floorId, floorNick, areas }) => ({
    floorId,
    floorNick,
    areas: areas.sort(byAreaOrder),
  }));
}

export function locationsToState(locations: readonly IssueLocation[]): ISelectionState {
  const selected = new Set<string>();
  const pins: Record<string, Pin[]> = {};
  for (const location of locations) {
    const key = areaKey(location.floor.floorId, location.area.areaId);
    selected.add(key);
    const points = location.area.selectedPoints;
    // A fresh array, so the edit state never aliases the issue's own locations.
    if (points && points.length > 0) pins[key] = [...points];
  }
  return { selected, pins };
}

/**
 * The picked areas as issue locations, in the site's area order — then every
 * `previous` location the site's areas can't resolve (the list hasn't loaded,
 * failed, or no longer has that area), kept as it was. The picker never showed
 * such a location, so the user can't have removed it: dropping it on close
 * would silently wipe the issue's locations, and with them its Safety flag.
 */
export function stateToLocations(
  state: ISelectionState,
  options: readonly IConfigArea[],
  previous: readonly IssueLocation[],
): IssueLocation[] {
  const known = new Set(options.map((area) => areaKey(area.floorId, area.areaId)));
  const picked = options
    .filter((area) => state.selected.has(areaKey(area.floorId, area.areaId)))
    .map((area) => toIssueLocation(area, state.pins[areaKey(area.floorId, area.areaId)]));
  const unresolved = previous.filter((l) => !known.has(areaKey(l.floor.floorId, l.area.areaId)));
  return [...picked, ...unresolved];
}

/** Pick or un-pick an area; un-picking drops its pins, so they don't come back
 *  if the area is picked again. */
export function toggleArea(state: ISelectionState, floorId: string, areaId: string): ISelectionState {
  const key = areaKey(floorId, areaId);
  const selected = new Set(state.selected);
  if (!selected.delete(key)) return { selected: selected.add(key), pins: state.pins };
  const { [key]: _dropped, ...pins } = state.pins;
  return { selected, pins };
}

/**
 * Drop a pin on an area, picking it. Only on a real area: Trusstor's floor
 * SVGs carry decorative outlines (wall corners, layout markers) whose ids
 * match `area*` but aren't in the site's areas — a pin there would orphan on
 * save, so the tap is a no-op.
 */
export function addPin(
  state: ISelectionState,
  options: readonly IConfigArea[],
  floorId: string,
  areaId: string,
  pin: Pin,
): ISelectionState {
  if (!options.some((a) => a.floorId === floorId && a.areaId === areaId)) return state;
  const key = areaKey(floorId, areaId);
  return {
    selected: new Set(state.selected).add(key),
    pins: { ...state.pins, [key]: [...(state.pins[key] ?? []), pin] },
  };
}

/** Un-pick every area on a floor, and its pins. */
export function clearFloor(state: ISelectionState, floor: IFloorGroup): ISelectionState {
  const keys = new Set(floor.areas.map((a) => areaKey(floor.floorId, a.areaId)));
  return {
    selected: new Set([...state.selected].filter((k) => !keys.has(k))),
    pins: Object.fromEntries(Object.entries(state.pins).filter(([k]) => !keys.has(k))),
  };
}

/** One floor's slice of the selection, keyed by bare area id — what its plan paints. */
export function floorSelection(
  state: ISelectionState,
  floor: IFloorGroup,
): { selectedAreaIds: Set<string>; pinsByArea: Record<string, Pin[]> } {
  const selectedAreaIds = new Set<string>();
  const pinsByArea: Record<string, Pin[]> = {};
  for (const area of floor.areas) {
    const key = areaKey(floor.floorId, area.areaId);
    if (state.selected.has(key)) selectedAreaIds.add(area.areaId);
    const pins = state.pins[key];
    if (pins) pinsByArea[area.areaId] = pins;
  }
  return { selectedAreaIds, pinsByArea };
}
